import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {QueryRef} from 'apollo-angular';




import { LazyLoadEvent } from 'primeng/api';
import { fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { FullProductFragment, ListProductsGQL, ListProductsQuery, ListProductsQueryVariables, Price, Product } from 'src/generated/graphql.generated';
import { SubSink } from 'subsink';

import { ConfigService } from '../core/public-api';
import { pagination } from '../global.constants';

import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaHelperService } from '../services/freya-helper.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { MutateProductComponent } from '../shared/mutate-product/mutate-product.component';
import { WatchQueryHelper } from '../utilities/watchQueryHelper';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit, OnDestroy {

  @ViewChild('mutateProduct') mutateProductRef: MutateProductComponent;
  @ViewChild('productSearchInput', { static: true }) productSearchInput!: ElementRef;

  subs = new SubSink();

  products: FullProductFragment[];

  showDeleted = false;

  // Lazy Loading Variables
  productsQueryRef: QueryRef<ListProductsQuery, ListProductsQueryVariables>;
  productsQH: WatchQueryHelper = {
    limit: pagination.defaultNumberOfItems,
    search: '',
    loading: true,
    total: 0
  };

  pagination = pagination;

  pricingEngineConfigQueryRef: ReturnType<typeof this.configService.watchConfigValues>;
  pricingEngine: {
    enabled: boolean;
    crewSizePrompt: string;
    itineraryPrompt: string;
    pricingPrompt: string;
    agentInstructionsPrompt: string;
    validationPrompt: string;
  } = undefined;

  constructor(
    private detailsHelper: DetailsHelperService,
    public freyaHelper: FreyaHelperService,
    private listProductsGQL: ListProductsGQL,
    private configService: ConfigService,
    private localNotify: FreyaNotificationsService,
  ) { }

  ngOnInit() {
    this.subs.sink = this.detailsHelper.getObjectUpdates('Prices').subscribe(() => this.productsQueryRef.refetch());

    this.subs.sink = fromEvent(this.productSearchInput.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      debounceTime(750),
    ).subscribe((text: string) => {
      this.productsQH.skip = 0;

      this.searchForProducts();
    });

    this.pricingEngineConfigQueryRef = this.configService.watchConfigValues({
      keys: [
        'pricingEngine.enabled',
        'pricingEngine.crewSizePrompt',
        'pricingEngine.itineraryPrompt',
        'pricingEngine.pricingPrompt',
        'pricingEngine.agentInstructionsPrompt',
        'pricingEngine.validationPrompt',
      ],
    });

    this.subs.sink = this.pricingEngineConfigQueryRef.valueChanges
    .subscribe((res) => {
      const values = res.data?.getConfigValues;
      const pricingEngine = {} as typeof this.pricingEngine;
      for (const value of values) {
        const [_, key]  = value.key.split('.');
        pricingEngine[key] = value.value;
      }

      this.pricingEngine = pricingEngine;
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  searchForProducts() {
    this.productsQH.loading = true;

    if (this.productsQueryRef) { // If we have already intialized the query
      // this.productsQueryRef.resetLastResults();
      this.productsQueryRef.refetch(this.getProductsInput());
    } else {
      this.initProductQuery();
    }
  }

  initProductQuery() {

    const watchProductsInput = this.getProductsInput();
    this.productsQueryRef = this.listProductsGQL.watch(watchProductsInput);

    this.subs.sink = this.productsQueryRef.valueChanges.subscribe((res) => {
      if (res.networkStatus === 7) {
        this.products = res.data.products.products;
        this.productsQH.loading = false;
        this.productsQH.total = res.data.products.total;
      }
    });
  }

  retrieveMoreProducts(event: LazyLoadEvent) {
    this.productsQH.limit = event.rows;
    this.productsQH.skip = event.first;

    this.searchForProducts();
  }

  getProductsInput(): ListProductsQueryVariables {
    return {
      filter: {
        names: [this.productsQH.search],
        showDeleted: this.showDeleted,
      },
      limit: this.productsQH.limit,
      skip: this.productsQH.skip,
    };
  }

  // Populate the Product and Price Information in the Side Panel
  viewProductDetails(product: Product, price?: Price) {
    if (price) {
      this.detailsHelper.open('price', price, true, undefined, { product });
      return;
    }
    this.detailsHelper.open('product', product);
  }

  openCreateProduct() {
    this.mutateProductRef.mutateType = 'create';
    this.mutateProductRef.openDialog();
  }

  updatePricingEngine() {
    const configs = Object.entries(this.pricingEngine)
      .map(([ key, value ]) => {
        return {
          key: `pricingEngine.${ key }`,
          value: (value || '') as string,
        };
      });

    this.subs.sink = this.configService.setConfigValues({
      configs,
    }).subscribe((res) => {
      this.localNotify.info(`Pricing engine updated.`);
    });
  }
}
